/**
 * Followed-hospitals storage (localStorage).
 *
 * Lets a user "follow" hospitals — their shortlist of real options to pre-plan
 * around (which hospital for which scenario, for them + their family). Mirrors
 * scenarioStorage/familyStorage: anon-friendly, instant, on-device. The same
 * shortlist is what a future per-hospital "year in review" report would draw on.
 */

export interface FollowedHospital {
  id: string;
  /** Cached for display so the Followed page renders without a lookup. */
  name: string;
  city: string;
  state: string;
  followedAt: number;
}

const KEY = "ahp_followed_hospitals";

function read(): FollowedHospital[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function write(list: FollowedHospital[]) {
  try { localStorage.setItem(KEY, JSON.stringify(list)); } catch { /* ignore */ }
  try { window.dispatchEvent(new Event("ahp-followed-changed")); } catch { /* ignore */ }
}

export const hospitalStorage = {
  getFollowed(): FollowedHospital[] {
    return read().sort((a, b) => b.followedAt - a.followedAt);
  },
  getFollowedIds(): Set<string> {
    return new Set(read().map((h) => h.id));
  },
  isFollowed(id: string): boolean {
    return read().some((h) => h.id === id);
  },
  follow(h: { id: string; name: string; city: string; state: string }) {
    const list = read();
    if (list.some((x) => x.id === h.id)) return;
    list.push({ id: h.id, name: h.name, city: h.city, state: h.state, followedAt: Date.now() });
    write(list);
  },
  unfollow(id: string) {
    write(read().filter((h) => h.id !== id));
  },
  /** @returns the new followed state (true = now following). */
  toggle(h: { id: string; name: string; city: string; state: string }): boolean {
    if (this.isFollowed(h.id)) { this.unfollow(h.id); return false; }
    this.follow(h); return true;
  },
  count(): number {
    return read().length;
  },
};
